/**
 * The Sending card, shared by the automation builder and a bulk send's
 * page: an automation's own daily email limit and warm-up ramp.
 */
import { Anchor, Card, Group, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useVocab } from '../../vocab';
import { GENTLE_RAMP, describeRamp, parseRamp, showRamp } from '../emails/warmup';

/** The most an automation's own daily limit (and each warm-up day) may be. */
export const MAX_DAILY_LIMIT = 100_000;

/**
 * How much this automation may send: at most N emails a day of its own, on
 * top of each brand's limit, and a warm-up ramp (day 1 is its first email
 * sent; the lower of the ramp and the daily limit wins). Saved with the
 * rest, though it makes no new version, and runs already going are held to
 * it too.
 */
export function SendingCard({
  dailyLimit, onChange, warmup, onWarmup,
}: { dailyLimit: number; onChange: (n: number) => void; warmup: string; onWarmup: (s: string) => void }) {
  const v = useVocab();
  const ramp = parseRamp(warmup);
  const tooBig = ramp.some((n) => n > MAX_DAILY_LIMIT);
  return (
    <Card padding="sm" withBorder data-testid="sending-card">
      <Stack gap={6}>
        <Text fw={600} size="sm">Sending</Text>
        <Group gap={6} align="center" wrap="wrap">
          <Text size="sm">At most</Text>
          <NumberInput
            size="xs"
            aria-label="At most this many emails a day"
            placeholder="No limit"
            min={1}
            max={MAX_DAILY_LIMIT}
            allowDecimal={false}
            allowNegative={false}
            clampBehavior="strict"
            thousandSeparator=","
            value={dailyLimit > 0 ? dailyLimit : ''}
            onChange={(n) => onChange(typeof n === 'number' && n > 0 ? n : 0)}
            w={110}
            data-testid="daily-limit"
          />
          <Text size="sm">{`emails a day from this ${v.one('workflow')}`}</Text>
        </Group>
        <Text size="xs" c="dimmed">
          {'Leave it empty for no limit of its own. Each brand\'s daily limit still applies. Emails over the limit wait until 08:00 the next day, unless their send step gives up first.'}
        </Text>
        <TextInput
          size="xs"
          label="Warm-up"
          description={`The most emails on day 1, 2, 3 and on, counted from this ${v.one('workflow')}'s first email. The last figure holds after that. Up to 60 days.`}
          placeholder={showRamp(GENTLE_RAMP)}
          value={warmup}
          onChange={(e) => onWarmup(e.currentTarget.value)}
          error={tooBig ? `At most ${MAX_DAILY_LIMIT.toLocaleString('en-GB')} a day.` : undefined}
          data-testid="warmup"
        />
        <Group gap="xs" align="center" wrap="wrap">
          <Text size="xs" c="dimmed" data-testid="warmup-words">
            {ramp.length ? describeRamp(ramp) : 'No warm-up: it can send up to its limit from the first day.'}
          </Text>
          {!warmup.trim() && (
            <Anchor component="button" type="button" size="xs" onClick={() => onWarmup(showRamp(GENTLE_RAMP))} data-testid="warmup-gentle">
              Use a gentle ramp
            </Anchor>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
